<template>
  <trix-toolbar :id="toolbarId">
    <!-- We are using flexbox layout to avoid having a horizontal scroll on narrow screens.
         In addition to avoiding a horizontal scrollbar for better usability of the toolbar tools,
         this is necessary because of toolbar components which require a vertical overflow out of the toolbar,
         such as the dropdown for inserting content blocks. Due to a bug in the implementation of CSS property
         "overflow" in browsers, we are not able to use a horizontal scroll while having vertical overflow be visible as we need to.
         See https://stackoverflow.com/a/17020278 and the comment in below CSS on .trix-button-row -->
    <div class="d-flex flex-wrap flex-lg-nowrap">
      <div class="trix-button-row">
        <span class="trix-button-group trix-button-group--text-tools" data-trix-button-group="text-tools">
          <bold-button />
          <italic-button />
          <link-button />
        </span>
        <span class="trix-button-group-spacer" />
        <span class="trix-button-group trix-button-group--block-tools" data-trix-button-group="block-tools">
          <h1-button v-if="showHeadingButton" />
          <quote-button />
          <code-button v-if="showCodeButton" />
          <ul-button />
          <ol-button />
          <outdent-button />
          <indent-button />
        </span>
      </div>
      <template v-if="showAttachmentsGroup">
        <div class="m-lg-2" />
        <div class="trix-button-row">
          <span class="trix-button-group trix-button-group--file-tools" data-trix-button-group="file-tools">
            <attach-files-button v-if="showAttachFilesButton" />
            <div class="content-insertion-component">
              <slot name="content-insertion-component" />
            </div>
          </span>
        </div>
      </template>
      <div class="m-2" />
      <div class="trix-button-row">
        <span v-if="showHtmlModeToggle" class="trix-button-group">
          <toolbar-button :button-text="htmlModeButtonText" @click="changeToHtmlMode" />
        </span>
        <span class="trix-button-group-spacer" />
        <span class="trix-button-group trix-button-group--history-tools" data-trix-button-group="history-tools">
          <undo-button />
          <redo-button />
        </span>
      </div>
    </div>
    <div class="trix-dialogs" data-trix-dialogs>
      <div class="trix-dialog trix-dialog--link" data-trix-dialog="href" data-trix-dialog-attribute="href">
        <div class="trix-dialog__link-fields">
          <input :type="hrefInputType" name="href" class="trix-input trix-input--dialog" :placeholder="lang.urlPlaceholder"
                 :aria-label="lang.url" required data-trix-input>
          <div class="trix-button-group">
            <input type="button" class="trix-button trix-button--dialog" :value="lang.link"
                   data-trix-method="setAttribute">
            <input type="button" class="trix-button trix-button--dialog" :value="lang.unlink"
                   data-trix-method="removeAttribute">
          </div>
        </div>
      </div>
    </div>
  </trix-toolbar>
</template>

<script>
import AttachFilesButton from './toolbar/attach-files-button'
import BoldButton from './toolbar/bold-button'
import CodeButton from './toolbar/code-button'
import H1Button from './toolbar/h1-button'
import IndentButton from './toolbar/indent-button'
import ItalicButton from './toolbar/italic-button'
import LinkButton from './toolbar/link-button'
import OlButton from './toolbar/ol-button'
import OutdentButton from './toolbar/outdent-button'
import QuoteButton from './toolbar/quote-button'
import RedoButton from './toolbar/redo-button'
import ToolbarButton from './toolbar/button'
import UlButton from './toolbar/ul-button'
import UndoButton from './toolbar/undo-button'

export default {
  components: { AttachFilesButton, BoldButton, CodeButton, H1Button, IndentButton, ItalicButton,
                LinkButton, OlButton, OutdentButton, QuoteButton, RedoButton, ToolbarButton, UlButton, UndoButton },

  props: {
    contentBlocks: {
      type: Array,
      required: false
    },

    showAttachFilesButton: {
      type: Boolean,
      required: false,
      default: true
    },

    showCodeButton: {
      type: Boolean,
      required: false,
      default: false
    },

    showHeadingButton: {
      type: Boolean,
      required: false,
      default: true
    },

    showHtmlModeToggle: {
      type: Boolean,
      required: false,
      default: true
    },

    toolbarId: {
      type: String,
      required: true
    },

    validateHref: {
      type: Boolean,
      required: false,
      default: true
    }
  },

  emits: ['change-to-html-mode'],

  data() {
    return {
      htmlModeButtonText: '<HTML>'
    }
  },

  computed: {
    hrefInputType() {
      // Setting the input type to 'url' will cause that the browser validates the value as a URL,
      // so set it to 'text' to allow any value if we want to skip the validation
      return this.validateHref ? 'url' : 'text'
    },

    lang() {
      return this.i18n.t('components.trix_editor.toolbar')
    },

    showAttachmentsGroup() {
      return this.showAttachFilesButton || !!this.$slots['content-insertion-component']
    }
  },

  methods: {
    changeToHtmlMode() {
      this.$emit('change-to-html-mode')
    }
  }

}
</script>

<style scoped lang="scss">
.trix-button-row {
  // We must use `overflow: visible` here, not `overflow-y: visible`, due to the following bug in how browsers implement overflow properties:
  // https://stackoverflow.com/a/17020278
  overflow: visible;
}

.content-insertion-component {
  $side-border: 1px solid #ccc;
  border-left: $side-border;
  height: 27px;
}
</style>
